/**
 * FoliarWorkspaceLayout — the single "Foliar" workspace (F1).
 *
 * Owns programme resolution (?programme= wins, else the tenant's active
 * programme) and the tab bar, and hands the resolved programme to each tab
 * through the router outlet context. Tabs are real routes, so per-menu RBAC
 * gating is unchanged: a tab renders only when its own menu is held.
 *
 * The Sign-off tab (F3) is programme-INDEPENDENT — it lists every awaiting
 * session for the tenant — so unlike the empty state and the programme
 * <select>, it is not gated on `activeTab === 'board'`.
 *
 * The error branch returns BEFORE the loading guard — a failed load must render
 * a notice, not sit on "loading" forever.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '../shell/PageHeader';
import { Notice, TabBar, TabPanel } from '../patterns';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { useMenu } from '../../auth/useMenu';
import { nutritionService, NutritionProgramme } from '../../services/nutritionService';
import './nutrition.css';

export interface FoliarWorkspaceContext {
  programme: NutritionProgramme | null;
  programmes: NutritionProgramme[];
  reload: () => void;
}

type TabKey = 'board' | 'signoff' | 'setup';
const PATH_BY_TAB: Record<TabKey, string> = {
  board: '/nutrition/foliar',
  signoff: '/nutrition/foliar/sign-off',
  setup: '/nutrition/foliar/setup',
};

const FoliarWorkspaceLayout: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedId = searchParams.get('programme');

  const [programmes, setProgrammes] = useState<NutritionProgramme[] | null>(null);
  const [programme, setProgramme] = useState<NutritionProgramme | null>(null);
  const [error, setError] = useState('');
  const [nonce, setNonce] = useState(0);

  const hasSessions = useMenu('fertilizing:sessions');
  const hasProgramme = useMenu('fertilizing:programme');
  const hasSignoff = useMenu('fertilizing:signoff');

  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('nutrition.title'), to: '/nutrition' },
    { label: t('nutrition.foliar.workspace.title') },
  ]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await nutritionService.listProgrammes();
        if (cancelled) return;
        setProgrammes(list);
        const requested = requestedId ? list.find((p) => p._id === requestedId) : undefined;
        // Fall back to the active-programme lookup only when NO ?programme= was
        // present at all. An id that names nothing resolves to null and shows
        // the empty state — a stale bookmark must not silently swap the user
        // onto a different programme's board.
        setProgramme(requested ?? (requestedId ? null : await nutritionService.getActiveProgramme()));
        setError('');
      } catch (_err) {
        if (!cancelled) { setProgrammes([]); setError(t('nutrition.foliar.workspace.loadFailed')); }
      }
    })();
    return () => { cancelled = true; };
    // `t` is deliberately absent: react-i18next's t is not referentially stable
    // and would re-fire this effect on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedId, nonce]);

  // True only when a specific programme was asked for, it did not resolve,
  // AND there is a list to choose from — the copy points at the selector,
  // which is itself gated on `programmes.length > 0` below, so this must
  // stay a conjunction of all three. With no programmes at all, an
  // unresolvable id falls through to the existing "no programme" copy: there
  // is no selector to point at, so "create one" remains the right advice.
  const requestedMissing = Boolean(requestedId) && !programme && (programmes?.length ?? 0) > 0;

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const context: FoliarWorkspaceContext = useMemo(
    () => ({ programme, programmes: programmes || [], reload }),
    [programme, programmes, reload],
  );

  // Order matters: '/nutrition/foliar/setup' and '/nutrition/foliar/sign-off'
  // share no prefix, so either test may come first — but keep 'board' as the
  // FALLBACK, never a positive test, or every non-tab foliar URL that reaches
  // this layout would light the Board tab.
  const activeTab: TabKey =
    location.pathname.startsWith('/nutrition/foliar/setup') ? 'setup'
      : location.pathname.startsWith('/nutrition/foliar/sign-off') ? 'signoff'
        : 'board';

  const onSelectTab = (key: string) => {
    const to = PATH_BY_TAB[key as TabKey];
    // Carry the chosen programme across tabs so switching back to the board does
    // not silently reset it to the active one.
    navigate(programme ? `${to}?programme=${programme._id}` : to);
  };

  if (error) {
    return (
      <div className="pat-page" data-testid="foliar-workspace-error">
        <PageHeader title={t('nutrition.foliar.workspace.title')} />
        <Notice tone="danger">{error}</Notice>
      </div>
    );
  }
  if (programmes === null) return <div className="pat-page">{t('common.loading')}</div>;

  return (
    <div className="pat-page" data-testid="foliar-workspace">
      <PageHeader
        title={t('nutrition.foliar.workspace.title')}
        meta={programme
          ? `${programme.season}${programme.source?.vendor ? ` · ${programme.source.vendor}` : ''}`
          : t('nutrition.foliar.workspace.noProgramme')}
        actions={activeTab === 'board' && programmes.length > 0 ? (
          <select
            className="ntr-resolve-select"
            data-testid="foliar-programme-select"
            aria-label={t('nutrition.foliar.workspace.programmeLabel')}
            value={programme?._id || ''}
            onChange={(e) => setSearchParams(e.target.value ? { programme: e.target.value } : {})}
          >
            <option value="">{t('nutrition.workspace.selectProgramme')}</option>
            {programmes.map((p) => (
              <option key={p._id} value={p._id}>
                {p.season}{p.source?.vendor ? ` · ${p.source.vendor}` : ''}
              </option>
            ))}
          </select>
        ) : undefined}
      />

      <TabBar activeKey={activeTab} onSelect={onSelectTab} ariaLabel={t('nutrition.foliar.workspace.title')}>
        {hasSessions ? (
          <TabPanel id="board" title={t('nutrition.foliar.workspace.tab.board')}>
            {activeTab === 'board' ? <Outlet context={context} /> : null}
          </TabPanel>
        ) : null}
        {hasSignoff ? (
          <TabPanel id="signoff" title={t('nutrition.foliar.workspace.tab.signOff')}>
            {activeTab === 'signoff' ? <Outlet context={context} /> : null}
          </TabPanel>
        ) : null}
        {hasProgramme ? (
          <TabPanel id="setup" title={t('nutrition.foliar.workspace.tab.setup')}>
            {activeTab === 'setup' ? <Outlet context={context} /> : null}
          </TabPanel>
        ) : null}
      </TabBar>

      {!programme && activeTab === 'board' && (
        <div className="ntr-empty" data-testid="foliar-workspace-empty">
          <div className="ntr-empty__message">
            {requestedMissing ? t('nutrition.workspace.programmeNotFound') : t('nutrition.foliar.workspace.emptyMessage')}
          </div>
          {!requestedMissing && hasProgramme && (
            <button type="button" className="pat-btn pat-btn--primary"
              onClick={() => navigate('/nutrition/foliar/setup')}>
              {t('nutrition.foliar.workspace.goToSetup')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default FoliarWorkspaceLayout;
